/**
 * @file inward-sla.ts
 * @module app/modules/inward/utils/inward-sla
 * @description Inward SLA settings and pending actions (dashboard card + settings tab):
 *   labels, who each SLA applies to, validation, and "overdue by / due in" text.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import { formatDuration } from "./inward-timeline";

export type InwardSlaAction =
  | "startInward"
  | "warehouseApproval"
  | "financeApproval"
  | "costAcknowledgement"
  | "sellingPriceUpdate";

export type InwardSla = Record<InwardSlaAction, number>;

export const SLA_MIN = 1;
export const SLA_MAX = 7 * 24 * 60;
/** Default minutes between reminder sounds while a pending action is overdue. */
export const REMINDER_DEFAULT_MINUTES = 60;

/** "" when the reminder interval is valid, else the message. */
export const checkReminder = (value: string): string => {
  const v = value.trim() === "" ? NaN : Number(value);
  return Number.isInteger(v) && v >= SLA_MIN && v <= SLA_MAX
    ? ""
    : `Reminder sound interval must be a whole number of minutes between ${SLA_MIN} and ${SLA_MAX}`;
};

/** Rows of the settings form: role, action, and when the clock starts. */
export const SLA_ROWS: {
  key: InwardSlaAction;
  role: string;
  action: string;
  from: string;
}[] = [
  {
    key: "startInward",
    role: "Loading bay manager",
    action: "Start inward",
    from: "after the manager, dock and date are assigned (from the start of the planned day)",
  },
  {
    key: "warehouseApproval",
    role: "Warehouse manager",
    action: "Warehouse approval",
    from: "after inward is done",
  },
  {
    key: "financeApproval",
    role: "Finance manager",
    action: "Finance approval",
    from: "after inward is done",
  },
  {
    key: "costAcknowledgement",
    role: "Procurement",
    action: "Acknowledge cost price change",
    from: "after Finance changes a cost price",
  },
  {
    key: "sellingPriceUpdate",
    role: "Procurement",
    action: "Update selling price (cost above selling price)",
    from: "after Finance approves a product whose cost is above its selling price",
  },
];

export const ACTION_TITLES: Record<InwardSlaAction, string> = {
  startInward: "Start inward",
  warehouseApproval: "Warehouse approval",
  financeApproval: "Finance approval",
  costAcknowledgement: "Cost acknowledgement",
  sellingPriceUpdate: "Selling price update",
};

/** First problem in the SLA form (values as typed), or null. */
export const checkSla = (
  values: Record<InwardSlaAction, string>
): string | null => {
  for (const row of SLA_ROWS) {
    const raw = String(values[row.key] ?? "").trim();
    const n = Number(raw);
    if (!/^\d+$/.test(raw) || n < SLA_MIN || n > SLA_MAX) {
      return `${row.action}: enter whole minutes between ${SLA_MIN} and ${SLA_MAX}`;
    }
  }
  return null;
};

export interface PendingActionItem {
  key: string;
  action: InwardSlaAction;
  title: string;
  detail: string;
  warehouseName?: string;
  warehouseId?: string;
  pendingWith: string;
  poId?: string;
  poRefKey?: string;
  grnId?: string;
  grnRefKey?: string;
  since: string;
  dueAt: string;
  slaMinutes: number;
  overdue: boolean;
  minutes: number;
}

export interface PendingActionsResponse {
  sla: InwardSla;
  /** minutes between reminder sounds while something is overdue (default 60) */
  reminderIntervalMinutes?: number;
  /** ding on / off for super admins (others always hear it); on unless switched off */
  reminderSoundEnabled?: boolean;
  summary: {
    total: number;
    overdue: number;
    byAction: Record<InwardSlaAction, { total: number; overdue: number }>;
  };
  items: PendingActionItem[];
  superAdmin: boolean;
}

/** "Overdue by 1h 05m" / "Due in 10m", recomputed against `now` so it stays live. */
export const dueText = (
  item: Pick<PendingActionItem, "dueAt">,
  now: Date = new Date()
): {
  overdue: boolean;
  text: string;
} => {
  const diff = now.getTime() - new Date(item.dueAt).getTime();
  if (Number.isNaN(diff)) return { overdue: false, text: "-" };
  return diff > 0
    ? { overdue: true, text: `Overdue by ${formatDuration(diff)}` }
    : { overdue: false, text: `Due in ${formatDuration(-diff)}` };
};
